import { defineStore } from 'pinia';
import { useRoute, useRouter } from 'nuxt/app';

/**
 * Allowed tag keys for resources
 */
export const ALLOWED_RESOURCE_TAGS = [
  'formatUseCase',
  'industryApplications',
  'visionCampus',
  'news'
];

/**
 * Store for managing resource search filters
 */
export const useResourcesStore = defineStore('resourcesSearch', {
  state: () => ({
    // Filters object with tag keys and boolean values
    filters: ALLOWED_RESOURCE_TAGS.reduce((acc, tag) => {
      acc[tag] = false;
      return acc;
    }, {} as Record<string, boolean>),

    // Available facets from the search response
    availableFacets: [] as string[],

    // Mapping of filter keys to their facet categories
    filterToFacetMapping: {} as Record<string, string>,

    // Search query
    query: '',

    // Loading state
    loading: false,

    queryId: '',
    indexName: ''
  }),

  getters: {
    /**
     * Get active filter tags as an array of strings
     */
    activeFilters: (state) => {
      return Object.entries(state.filters)
        .filter(([_, value]) => value)
        .map(([key]) => key);
    },

    /**
     * Check if any filters are active
     */
    hasActiveFilters: (state) => {
      return Object.values(state.filters).some((value) => value);
    }
  },

  actions: {
    /**
     * Update available facets from search response
     * @param facets Array of facet names or filter values
     */
    updateAvailableFacets(facets: string[]) {
      this.availableFacets = facets;
      // Initialize filters for new facets
      facets.forEach((facet) => {
        if (!(facet in this.filters)) {
          this.filters[facet] = false;
        }
      });
    },

    /**
     * Update available filters from categorized search response
     * @param filters Array of filter objects with name and values
     */
    updateAvailableFilters(filters: any[]) {
      const allFilterKeys: string[] = [];
      const newFilterToFacetMapping: Record<string, string> = {};

      // Extract all filter keys from the categorized response and track their facet categories
      filters.forEach((category) => {
        if (category.values) {
          category.values.forEach((filterValue: any) => {
            // Extract the key from values like "AOI, Fehlererkennung & Qualitätskontrolle<?>aoi"
            const filterKey = filterValue.value.replace('<?>', '##_filter_##');

            allFilterKeys.push(filterKey);

            // Map the filter key to its facet category
            newFilterToFacetMapping[filterKey] = category.name;
          });
        }
      });

      this.availableFacets = allFilterKeys;
      this.filterToFacetMapping = newFilterToFacetMapping;

      // Initialize filters for new facets
      allFilterKeys.forEach((facet) => {
        if (!(facet in this.filters)) {
          this.filters[facet] = false;
        }
      });
    },

    /**
     * Toggle a filter by key
     * @param key The filter key to toggle
     */
    toggleFilter(key: string) {
      if (
        this.availableFacets.includes(key) ||
        ALLOWED_RESOURCE_TAGS.includes(key)
      ) {
        this.filters[key] = !this.filters[key];
        this.syncToQueryParams();
      }
    },

    /**
     * Set a filter value by key
     * @param key The filter key to set
     * @param value The boolean value to set
     */
    setFilter(key: string, value: boolean) {
      if (
        this.availableFacets.includes(key) ||
        ALLOWED_RESOURCE_TAGS.includes(key)
      ) {
        this.filters[key] = value;
        this.syncToQueryParams();
      }
    },

    /**
     * Reset all filters to false
     */
    resetFilters() {
      // Reset both legacy allowed tags and dynamic facets
      const allKeys = [
        ...new Set([...ALLOWED_RESOURCE_TAGS, ...this.availableFacets])
      ];
      allKeys.forEach((tag) => {
        this.filters[tag] = false;
      });
      this.syncToQueryParams();
    },

    /**
     * Reset all filters to false without syncing to query parameters
     * Used when we're already handling a route change to prevent duplicate route updates
     */
    resetFiltersWithoutSync() {
      // Reset both legacy allowed tags and dynamic facets
      const allKeys = [
        ...new Set([...ALLOWED_RESOURCE_TAGS, ...this.availableFacets])
      ];
      allKeys.forEach((tag) => {
        this.filters[tag] = false;
      });
      // Don't call syncToQueryParams() to avoid duplicate route changes
    },

    /**
     * Build facet filters object for Algolia search
     * @returns Object with facet names as keys and arrays of selected values
     */
    buildFacetFilters(): Record<string, string[]> {
      const facetFilters: Record<string, string[]> = {};

      // Group active filters by their actual facet category
      this.activeFilters.forEach((filterKey) => {
        // Get the facet category for this filter key
        const facetCategory = this.filterToFacetMapping[filterKey] || 'tags'; // Default to 'tags' for legacy filters

        // Initialize the facet category array if it doesn't exist
        if (!facetFilters[facetCategory]) {
          facetFilters[facetCategory] = [];
        }

        // Add the filter key to the appropriate facet category
        facetFilters[facetCategory].push(filterKey);
      });

      return facetFilters;
    },

    /**
     * Sync the current filters to URL query parameters
     */
    syncToQueryParams() {
      const router = useRouter();
      const route = useRoute();

      // Group active filters by facet category
      const facetFilters = this.buildFacetFilters();

      // Create a new query object based on the current route
      const newQuery = { ...route.query };

      // Remove the page parameter if it exists (reset to page 1 when filters change)
      delete newQuery.page;

      // Remove existing facet filter parameters
      delete newQuery.resourceTags; // Legacy parameter
      delete newQuery.tags;
      delete newQuery.detailed_topics;
      delete newQuery.topics;

      // Add facet filters as separate query parameters
      Object.entries(facetFilters).forEach(([facetName, filterValues]) => {
        if (filterValues.length > 0) {
          newQuery[facetName] = filterValues.join(',');
        }
      });

      // Update the URL with the selected filters
      router.push({
        query: newQuery
      });
    },

    /**
     * Update filters from URL query parameters
     */
    updateFromQueryParams() {
      const route = useRoute();

      // Reset all filters first (both legacy and dynamic)
      const allKeys = [
        ...new Set([...ALLOWED_RESOURCE_TAGS, ...this.availableFacets])
      ];
      allKeys.forEach((tag) => {
        this.filters[tag] = false;
      });

      // Handle legacy resourceTags parameter for backward compatibility
      const resourceTags = route.query.resourceTags;
      if (resourceTags) {
        const tags = Array.isArray(resourceTags)
          ? resourceTags.join(',').split(',')
          : resourceTags.split(',');

        tags.forEach((tag) => {
          if (
            ALLOWED_RESOURCE_TAGS.includes(tag) ||
            this.availableFacets.includes(tag)
          ) {
            this.filters[tag] = true;
          }
        });
      }

      // Handle separate facet parameters
      const facetCategories = ['tags', 'detailed_topics', 'topics'];
      facetCategories.forEach((facetCategory) => {
        const facetParam = route.query[facetCategory];
        if (facetParam) {
          const filterValues = Array.isArray(facetParam)
            ? facetParam.join(',').split(',')
            : facetParam.split(',');

          filterValues.forEach((filterValue) => {
            if (
              ALLOWED_RESOURCE_TAGS.includes(filterValue) ||
              this.availableFacets.includes(filterValue)
            ) {
              this.filters[filterValue] = true;
            }
          });
        }
      });
    }
  },

  // Enable persistence to keep filters between page reloads
  persist: true
});
